import { dehydrate, QueryClient } from '@tanstack/react-query';
import {
    AspectRatio,
    Box,
    Flex,
    Heading,
    Hero,
    HtmlContent,
    Meta,
    Skeleton,
    Stack,
    Video,
    Wrapper,
} from 'designsystem';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { ReportDetailQuery, ReportDetailQueryVariables, useReportDetailQuery } from '../../../gql/api';
import {
    apiImageToOpenGraph,
    authOptions,
    fetchApiData,
    getMetaForReport,
    gtm,
    slugifyTitle,
    useGetApiImageProps,
} from 'shared';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import { useIntl } from 'react-intl';
import { useEffect, useMemo } from 'react';
import prefetchGlobals from '../../../utils/prefetchGlobals';
import { unstable_getServerSession } from 'next-auth';
import { NextSeo } from 'next-seo';
import fetchArchiveUri from '../../../utils/fetchArchiveUri';
import { useArchivePathQuery } from '../../../gql/cms';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import InstituteBreadcrumbs from '../../../components/InstituteBreadcrumbs';
import striptags from 'striptags';

const ReportArchiveDetail = () => {
    const { query, asPath } = useRouter();
    const { locale } = useIntl();

    const { data, isLoading } = useReportDetailQuery({
        id: query.id as string,
        language: locale,
    });

    const archiveUri = useArchivePathQuery({
        site: siteHandleByLocale[locale],
    }).data.entry.uri;
    const report = data ? data.film : null;

    const getImageProps = useGetApiImageProps();
    const heroImgProps = useMemo(
        () => getImageProps(report?.publications.favoriteImage, report?.fullPreferredTitle, '100vw'),
        [report?.fullPreferredTitle, report?.publications.favoriteImage, getImageProps]
    );
    const ogImage = apiImageToOpenGraph(report.publications.favoriteImage);

    useEffect(() => {
        gtm.pageView(asPath, {
            page_pillar: 'institute',
            page_language: locale,
            page_navigation: archiveUri,
            film_name: report?.fullPreferredTitle,
            film_id: report?.id,
            film_edition: report?.edition.name,
        });
    }, [archiveUri, asPath, report, locale]);
    return (
        <>
            {report && (
                <NextSeo
                    title={report.fullPreferredTitle}
                    description={striptags(report.description?.translation)}
                    canonical={`${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/report/${query.id}/${slugifyTitle(
                        report.fullPreferredTitle
                    )}`}
                    openGraph={{
                        images: ogImage,
                    }}
                    titleTemplate="%s | IDFA Archive"
                />
            )}
            <Hero
                image={heroImgProps}
                topChildren={
                    <InstituteBreadcrumbs
                        parentUri={archiveUri}
                        currentPage={{
                            title: report.fullPreferredTitle,
                            uri: asPath,
                        }}
                        onImage
                    />
                }
            >
                <Stack mt={4}>
                    <Stack mt={2}>
                        <Heading as="h1" variant={1} clamp={1}>
                            {report.fullPreferredTitle}
                        </Heading>
                    </Stack>
                    <Box>
                        <Meta {...getMetaForReport(report)} size="m" />
                    </Box>
                </Stack>
            </Hero>
            <Wrapper>
                <Box maxW={['100%', null, '50%']} py={[9, null, 11]}>
                    <HtmlContent html={report.reportText?.translation} />
                </Box>
                <Flex flexDirection="column" mb={[9, null, 11]}>
                    {!isLoading &&
                        report.publications &&
                        report.publications.archiveVideos?.map(video => (
                            <Box key={video.id} mb={4}>
                                <Video url={video.value} />
                            </Box>
                        ))}
                    {isLoading && (
                        <AspectRatio ratio={16 / 9}>
                            <Skeleton height="100%" width="100%" />
                        </AspectRatio>
                    )}
                </Flex>
            </Wrapper>
        </>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, locale, req, res }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
        language: locale,
    };
    const session = await unstable_getServerSession(req, res, authOptions);
    const key = useReportDetailQuery.getKey(variables);
    const data = await queryClient.fetchQuery(key, () =>
        fetchApiData<ReportDetailQuery, ReportDetailQueryVariables>({
            locale,
            query: useReportDetailQuery.document,
            variables,
            session,
            req,
            res,
        })
    );
    if (!data.film || !data.film?.isAlternativeContent) {
        return {
            notFound: true,
        };
    }

    const archiveUri = await fetchArchiveUri(queryClient, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: archiveUri });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
            session,
        },
    };
};

export default ReportArchiveDetail;
